/**
 * Things you can SHOUT at coπ (Gordon 2026-11-05: "fun music related
 * expressions that start, do or open something — 'Jungle is massive!' plays a
 * random jungle track and starts a list with just jungle tracks; 'Hip hop you
 * don't stop!' the same; 'Meters!' opens the meters; 'Tuner'; 'show open
 * tuning'; 'Menu!'; 'Player!'"). Pure; matched on coπ's normalised text
 * (lowercase, no accents or punctuation) before the other rules.
 *
 * Expressions are the music's own catchphrases, so they work whatever
 * language the app is in. A genre shout lists fall-back genres: the first
 * one the catalogue has plays, with a queue of only that genre.
 */
import type { CopiAction } from './copi-commands';

export interface CopiShout {
  /** How the list shows it. */
  say: string;
  re: RegExp;
  action: CopiAction;
}

const genre = (...genres: string[]): CopiAction => ({ type: 'media', kind: 'any', genre: genres[0], orGenres: genres.slice(1) });

export const COPI_SHOUTS: CopiShout[] = [
  { say: 'Jungle is massive!', re: /^(the )?jungle (is|s|ist?|es|e) massive?$/, action: genre('Jungle', 'Drum & Bass', 'Drum and Bass', 'Breakbeat') },
  { say: 'Hip hop, you don’t stop!', re: /^hip ?hop (you )?(dont|do not|never) stop$/, action: genre('Hip-Hop', 'Hip Hop', 'Rap', 'Boom Bap') },
  { say: 'Boom bap!', re: /^boom ?bap$/, action: genre('Boom Bap', 'Hip-Hop', 'Hip Hop') },
  { say: 'Drum and bass, baby!', re: /^(drum (and|n|&) bass|dnb|d n b) baby$/, action: genre('Drum & Bass', 'Drum and Bass', 'Jungle') },
  { say: 'One love!', re: /^one love$/, action: genre('Reggae', 'Dancehall', 'Dub') },
  { say: 'House music all night long!', re: /^house music( all night long)?$/, action: genre('House', 'Deep House', 'Afro House') },
  { say: 'Get funky!', re: /^(get|make it|lets get) funky$/, action: genre('Funk', 'Soul') },
  { say: 'Soul power!', re: /^soul power$/, action: genre('Soul', 'R&B', 'Funk') },
  { say: 'Disco inferno!', re: /^disco( inferno| fever)?$/, action: genre('Disco', 'Funk') },
  { say: 'Techno, techno, techno!', re: /^techno( techno)+$/, action: genre('Techno', 'Electronic') },
  { say: 'Jazz it up!', re: /^jazz it up$/, action: genre('Jazz', 'Jazz Fusion', 'Soul Jazz') },
  { say: 'Pull up, selecta!', re: /^(pull up|rewind|wheel (it )?up|run it back|bring it back)( selecta| selector| dj)?$/, action: { type: 'restart' } },
  { say: 'Selecta!', re: /^(selecta|selector|play me something|dj play something)$/, action: { type: 'media', kind: 'any', random: true } },
  { say: 'Drop the needle!', re: /^drop the (needle|beat)$/, action: { type: 'play' } },
  { say: 'Studio time!', re: /^studio time$/, action: { type: 'navigate', href: '/studio', name: 'Studio' } },
  { say: 'Lights, camera, action!', re: /^lights camera action$/, action: { type: 'navigate', href: '/film-maker', name: 'Film Maker' } },
  { say: 'Let’s jam!', re: /^(lets|let us) jam$/, action: { type: 'navigate', href: '/studio/jam-session', name: 'Jam Session' } },
  { say: 'Showtime!', re: /^(its )?show ?time$/, action: { type: 'navigate', href: '/events', name: 'Events' } },
];

/** Single words that open something: "Meters!", "Player!", "Menu!", "Tuner". */
const WORDS: { re: RegExp; action: CopiAction }[] = [
  { re: /^(the |le |les |el |los |la )?(meters?|vu|vu meters?|visuali[sz]ers?|matare|matarna|mataren|vumatare|medidor|medidores|vumetros?|vumetres?)$/, action: { type: 'meter' } },
  { re: /^(the |le |les |el |los |la )?(player|media player|music player|spelaren|spelare|mediaspelaren|reproductor|el reproductor|lecteur|le lecteur)$/, action: { type: 'player' } },
];

/** A guitar (or bass, ukulele …) tuning: "show open tuning", "drop D", "DADGAD", "tune my bass". */
const LEAD = '(?:(?:show|show me|open|give me|pull up|bring up|go to|visa|visa mig|oppna|abre|muestra|muestrame|ouvre|montre|montre moi) )?(?:(?:the|an?|en|ett|la|el|le|une?) )?';
const GUITAR_TUNINGS: [RegExp, string][] = [
  [/^open (g|gee)( tuning)?$/, 'openG'], [/^open (d|dee)( tuning)?$/, 'openD'], [/^open e( tuning)?$/, 'openE'],
  [/^open a( tuning)?$/, 'openA'], [/^open c( tuning)?$/, 'openC'],
  [/^(open tuning|open guitar tuning|oppen stamning|afinacion abierta|accordage ouvert)$/, 'openG'],
  [/^drop (d|dee)( tuning)?$/, 'dropD'], [/^drop c( tuning)?$/, 'dropC'], [/^d ?a ?d ?g ?a ?d( tuning)?$/, 'dadgad'],
  [/^(guitar tuner|guitar tuning|standard tuning|tune (my |the )?guitar|gitarrstamning|stam (min |gitarren|gitarr)|afinador de guitarra|afina (la|mi) guitarra|accordeur de guitare|accorde (ma |la )?guitare)$/, 'standard'],
];
const OTHER: [RegExp, string][] = [
  [/^(bass tuner|tune (my |the )?bass|stam basen|afina (el|mi) bajo|accorde (ma |la )?basse)$/, 'bass'],
  [/^(ukulele tuner|tune (my |the )?ukulele|stam ukulelen|afina (el|mi) ukelele|accorde (mon |le )?ukulele)$/, 'ukulele'],
  [/^(violin tuner|tune (my |the )?violin|stam fiolen|afina (el|mi) violin|accorde (mon |le )?violon)$/, 'violin'],
];

export function matchShout(text: string): CopiAction | null {
  for (const s of COPI_SHOUTS) if (s.re.test(text)) return s.action;
  for (const w of WORDS) if (w.re.test(text)) return w.action;
  // As said ("open G tuning") and without a leading verb ("show open tuning").
  for (const t of [text, text.replace(new RegExp('^' + LEAD), '')]) {
    for (const [re, tuning] of GUITAR_TUNINGS) if (re.test(t)) return { type: 'tuner', instrument: 'guitar', tuning };
    for (const [re, instrument] of OTHER) if (re.test(t)) return { type: 'tuner', instrument, tuning: 'standard' };
  }
  return null;
}
